<template>
  <div class="working-hours-page">
    <a-row type="flex" justify="space-between">
      <a-col :lg="8" :md="8" :sm="10" :xs="24">
        <h2 class="list-title">
          <a-icon type="clock-circle" class="list-icon" />
          <span>{{ labels.title }}</span>
        </h2>
        <p class="list-subtitle">{{ labels.subtitle }}</p>
      </a-col>
      <a-col :lg="16" :md="16" :xs="24">
        <a-row
          type="flex"
          justify="end"
          :gutter="[0, 8]"
          :style="{
            alignItems: 'flex-end',
            marginTop: '25px',
            marginBottom: '10px',
            textAlign: 'right',
          }"
          class="row-reverse"
        >
          <a-col :xs="24" :md="13" :lg="15">
            <a-input-search
              :placeholder="labels.placeholderSearch"
              style="width: 100%"
              @search="handleSearch"
              allowClear
            />
          </a-col>
        </a-row>
      </a-col>
    </a-row>
    <a-row>
      <a-col :span="24">
        <StandardTable
          ref="standardTable"
          :fetchService="fetchWorkingHours"
          :columns="columns"
          :locale="locale"
          :handleShowTotal="handleShowTotal"
          v-bind="!isMobile && { scroll: { x: true } }"
          :paginationType="isMobile ? 'arrows' : 'numbers'"
          :customRow="handleCustomRow"
          :showSizeChanger="true"
          :pageSizeOptions="['10', '20', '50', '100']"
        />
      </a-col>
    </a-row>
  </div>
</template>

<script>
import _ from "lodash";
import labels from "@/utils/labels";
import StandardTable from "../../../components/core/table/StandardTable.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { DATE_FORMAT, adminPageTitle } from "../../../utils/utils";
import moment from "moment-timezone";
export default {
  components: { StandardTable },
  name: "WorkingHourList",
  metaInfo: {
    title: adminPageTitle("Horarios"),
  },
  filters: {
    ucwords(value) {
      if (!value) return "";
      value = value.toString();
      return value
        .split(" ")
        .map((item) => _.capitalize(item))
        .join(" ");
    },
  },
  data() {
    return {
      labels: labels.workingHours,
      search: "",
      activeRow: null,
    };
  },
  methods: {
    ...mapActions("workingHours", ["fetchWorkingHours"]),
    handleReloadTable() {
      let filtersTable = this.$refs.standardTable.filters;
      let result = { ...filtersTable };
      this.$refs.standardTable.handleFilters(result);
    },
    handleSearch(search) {
      this.$refs.standardTable.handleSearch(search);
    },
    handleShowTotal(total, range) {
      return `${range[0]}-${range[1]} de ${total}`;
    },
    handleCustomRow(_, index) {
      return {
        on: {
          click: () => {
            this.activeRow = this.activeRow === index ? null : index;
          },
        },
      };
    },
  },
  computed: {
    ...mapGetters("adminAuth", ["getLoggedUser"]),
    ...mapState("layout", ["screenWidth"]),
    isMobile() {
      return this.screenWidth < 600;
    },
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.title,
          dataIndex: "title",
          key: "title",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return `${text}`;
          },
        },
        {
          title: this.labels.table.columns.description,
          dataIndex: "description",
          key: "description",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            const shortenedText = text
              ? text.length > 30
                ? `${text.substring(0, 30)}...`
                : text
              : "N/A";
            return <span title={text}>{shortenedText}</span>;
          },
        },
        {
          title: this.labels.table.columns.today_enabled.title,
          dataIndex: "today_enabled",
          key: "today_enabled",
          sorter: false,
          hidden: this.isMobile,
          customRender: (text) => {
            return (
              <a-tag color={text ? "green" : "red"}>
                {text
                  ? this.labels.table.columns.today_enabled.yes
                  : this.labels.table.columns.today_enabled.no}
              </a-tag>
            );
          },
        },
        {
          title: this.labels.table.columns.today_start,
          dataIndex: "today_start",
          key: "today_start",
          sorter: false,
          hidden: this.isMobile,
          customRender: (text) => {
            return text
              ? moment(text, DATE_FORMAT.SERVER_TIME).format(
                  DATE_FORMAT.MOMENT_TIME
                )
              : "N/A";
          },
        },
        {
          title: this.labels.table.columns.today_end,
          dataIndex: "today_end",
          key: "today_end",
          sorter: false,
          hidden: this.isMobile,
          customRender: (text) => {
            return text
              ? moment(text, DATE_FORMAT.SERVER_TIME).format(
                  DATE_FORMAT.MOMENT_TIME
                )
              : "N/A";
          },
        },
        {
          title: this.labels.table.columns.status.title,
          dataIndex: "status",
          key: "status",
          hidden: this.isMobile,
          customRender: (text) => {
            return (
              <a-tag color={text ? "green" : "red"}>
                {this.labels.table.columns.status[text ? "active" : "inactive"]}
              </a-tag>
            );
          },
        },
        {
          title: this.labels.table.columns.action.title,
          dataIndex: "action",
          key: "action",
          width: "120px",
          hidden: this.isMobile,
          customRender: (_, record) => {
            return (
              <div class="action-buttons">
                <router-link
                  to={{
                    name: "admin.working-hours.edit",
                    params: { id: record.id },
                  }}
                  title={this.labels.table.columns.action.edit}
                >
                  <a-button
                    type="link"
                    icon="edit"
                    class="action-button-edit"
                  />
                </router-link>
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.workingHours,
          dataIndex: "workingHours",
          key: "workingHours",
          sorter: false,
          hidden: !this.isMobile,
          customRender: (_text, record, index) => {
            // Card item design
            const color = record.status ? "#0ca750" : "#f50";
            const weekday = moment()
              .tz(DATE_FORMAT.TIMEZONE)
              .format("dddd")
              ?.toLowerCase();
            const start = record[`${weekday}_start`]
              ? moment(record[`${weekday}_start`], DATE_FORMAT.SERVER_TIME)
              : null;
            const end = record[`${weekday}_end`]
              ? moment(record[`${weekday}_end`], DATE_FORMAT.SERVER_TIME)
              : null;
            const shortenedText = record.description
              ? record.description.length > 30
                ? `${record.description.substring(0, 30)}...`
                : record.description
              : "N/A";
            return (
              <div class="list-item">
                <div class="list-item-head">
                  <div
                    class="list-item-head-header"
                    style={{ borderColor: color }}
                  >
                    <span>
                      {this.$options.filters.ucwords(moment().format("dddd"))}
                    </span>
                    <small>
                      {start ? start.format(DATE_FORMAT.MOMENT_TIME) : "N/A"} -{" "}
                      {end ? end.format(DATE_FORMAT.MOMENT_TIME) : "N/A"}
                    </small>
                  </div>
                  <div class="list-item-head-content">
                    <div class="list-item-head-content-title">
                      <span>{record.title}</span>
                    </div>
                    <div class="list-item-head-content-description">
                      <span title={record.description}>{shortenedText}</span>
                    </div>
                  </div>
                  <div class="list-item-head-footer">
                    <div class="list-item-head-footer-status">
                      <a-tag color={color}>
                        {
                          this.labels.table.columns.status[
                            record.status ? "active" : "inactive"
                          ]
                        }
                      </a-tag>
                    </div>
                  </div>
                </div>
                {this.activeRow === index && (
                  <div class="list-item-actions">
                    <a-button-group>
                      <router-link
                        class="edit-button ant-btn ant-btn-primary ant-btn-block"
                        to={{
                          name: "admin.working-hours.edit",
                          params: { id: record.id },
                        }}
                        title={this.labels.table.columns.action.edit}
                      >
                        {this.labels.table.columns.action.edit}
                      </router-link>
                    </a-button-group>
                  </div>
                )}
              </div>
            );
          },
        },
      ].filter((column) => !column.hidden);
    },
    serverFormat() {
      return "HH:mm:ss";
    },
  },
};
</script>
<style lang="scss">
@import "../../../assets/scss/variable";
.working-hours-page {
  .list {
    &-title {
      margin-bottom: 10px;
      .list-icon {
        margin-right: 10px;
      }
    }
    &-subtitle {
      margin-bottom: 10px;
    }
  }
  .add-button {
    color: color(primary);
    border-color: color(primary);
    margin-left: 10px;
  }
  .action-buttons {
    display: flex;
    justify-content: space-between;
    .action-button-edit {
      color: color(primary);
    }
    .action-button-delete {
      color: color(danger);
    }
  }
  @media screen and (max-width: $screen-mobile) {
    .row-reverse {
      flex-direction: column-reverse;
    }
  }
  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 25%;
        width: 25%;
        border-right: 4px solid color(border);
        .success {
          color: color(success);
        }
        .warning {
          color: color(warning);
        }
        .danger {
          color: color(danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 50%;
        width: 50%;
        overflow: hidden;
        &-title {
          span {
            font-size: 16px;
            font-weight: 600;
            text-overflow: ellipsis;
          }
        }
        &-description {
          span {
            font-size: 14px;
            font-weight: 500;
            text-overflow: ellipsis;
          }
        }
      }
      &-footer {
        max-width: 25%;
        width: 25%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: right;
        gap: 2px;
        &-status {
          width: 100%;
          span {
            font-size: 12px;
            font-weight: 400;
          }
          .ant-tag {
            padding: 0;
            margin: 0;
            padding-left: 2px;
            padding-right: 2px;
          }
        }
      }
    }
    &-actions {
      width: 100%;
      margin-top: 10px;
      .ant-btn-group {
        width: 100%;
        .more-info-button,
        .edit-button,
        .delete-button {
          width: 100%;
        }
      }
    }
  }
}
.w-100 {
  display: block;
  width: 100%;
  margin-bottom: 5px;
}
</style>
